import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { usePWA } from "@/hooks/use-pwa";
import { InstallPrompt } from "./install-prompt";
import { MigrationPrompt } from "./migration-prompt";
import { BottomTabBar } from "./bottom-tab-bar";

// 브라우저에서도 접근 가능한 경로들
const ALLOWED_BROWSER_PATHS = ["/term", "/support"];

// 탭바를 표시할 경로들
const TAB_VISIBLE_PATHS = ["/main", "/settings"];

interface PWAGateProps {
  children: React.ReactNode;
}

/**
 * PWA 접근 제어 게이트
 * - PWA 비활성화 시: 마이그레이션 안내 표시
 * - 일반 브라우저: 설치 안내 표시 (허용된 경로 제외)
 * - PWA/앱: 정상 렌더링 + 하단 탭바
 */
export function PWAGate({ children }: PWAGateProps) {
  const { isPWA, isInApp, isBrowser } = usePWA();
  const location = useLocation();
  const pathname = location.pathname;
  const [mounted, setMounted] = useState(false);

  const handleMounted = useCallback(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    handleMounted();
  }, [handleMounted]);

  const isPWAEnabled = import.meta.env.VITE_PWA_ENABLED !== "false";

  // 허용된 경로인지 확인
  const isAllowedPath = ALLOWED_BROWSER_PATHS.some((path) =>
    pathname.startsWith(path)
  );

  // 탭바를 표시할 경로인지 확인
  const shouldShowTabBar =
    isPWA &&
    !isInApp &&
    TAB_VISIBLE_PATHS.some((path) => pathname.startsWith(path));

  // 초기 마운트 전에는 children만 렌더링 (hydration mismatch 방지)
  if (!mounted) {
    return <>{children}</>;
  }

  // 1. PWA 비활성화 (마이그레이션 모드) - 허용된 경로 제외하고 앱 설치 안내
  if (!isPWAEnabled && !isInApp && !isAllowedPath) {
    return <MigrationPrompt />;
  }

  // 2. 일반 브라우저 접속 - 허용된 경로 제외하고 PWA 설치 안내
  // 로컬 개발 환경 및 E2E 테스트 환경에서는 PWA 설치 안내 스킵
  const isE2ETest = import.meta.env.VITE_E2E_TEST === "true";
  if (isBrowser && !isAllowedPath && !isE2ETest && !import.meta.env.DEV) {
    return <InstallPrompt />;
  }

  // 3. PWA 또는 앱에서 접속 - 정상 렌더링
  return (
    <>
      {/* 메인 콘텐츠 - 탭바가 표시될 때만 하단 패딩 추가 */}
      <div className={shouldShowTabBar ? "pb-[60px]" : ""}>{children}</div>

      {/* PWA에서만 하단 탭바 표시 */}
      <BottomTabBar />
    </>
  );
}
